import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import logo from '../assets/logo.png';
import { trackCTAClick } from '../utils/analytics.js';

/**
 * Navbar component renders the top navigation bar. It displays the company logo
 * and navigation links that scroll to different sections of the page. On
 * smaller screens a hamburger menu opens a sidebar overlay with glassmorphism styling.
 */
const Navbar = ({ onLinkClick }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
    if (onLinkClick) onLinkClick();
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Scroll detection for transparent → frosted glass transition
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial position
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <header className={`navbar${isScrolled ? ' scrolled' : ''}`} id="navbar">
        <div className="logo">
          <Link to="/" onClick={handleLinkClick}>
            <img src={logo} alt="Aakeeo Technologies logo" width="150" height="50" />
          </Link>
        </div>
        <nav className="desktop-nav">
          <ul>
            <li><NavLink to="/services" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Solutions</NavLink></li>
            <li className="nav-dropdown-parent">
              <NavLink to="/suitecrm" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>SuiteCRM <i className="fa-solid fa-chevron-down" style={{ fontSize: '0.6rem', marginLeft: '4px', opacity: 0.5 }}></i></NavLink>
              <div className="nav-dropdown">
                <span className="nav-dropdown-label">Services</span>
                <Link to="/services/suitecrm-implementation" onClick={handleLinkClick} className="nav-dropdown-item">
                  <div>
                    <strong>Implementation</strong>
                    <small>Deploy & configure from scratch</small>
                  </div>
                </Link>
                <Link to="/services/suitecrm-customisation" onClick={handleLinkClick} className="nav-dropdown-item">
                  <div>
                    <strong>Customisation</strong>
                    <small>Custom modules & workflows</small>
                  </div>
                </Link>
                <Link to="/services/crm-integration" onClick={handleLinkClick} className="nav-dropdown-item">
                  <div>
                    <strong>Integration</strong>
                    <small>Connect ERPs, WhatsApp & more</small>
                  </div>
                </Link>
                <Link to="/services/ai-workflow-automation" onClick={handleLinkClick} className="nav-dropdown-item">
                  <div>
                    <strong>AI Workflow Automation</strong>
                    <small>Lead scoring & automated follow-ups</small>
                  </div>
                </Link>
                <Link to="/services/suitecrm-support" onClick={handleLinkClick} className="nav-dropdown-item">
                  <div>
                    <strong>Support & Maintenance</strong>
                    <small>Upgrades, patches & monitoring</small>
                  </div>
                </Link>
                <div style={{ borderTop: '1px solid var(--border-light)', margin: '8px 0' }}></div>
                <span className="nav-dropdown-label">Open Source</span>
                <Link to="/suitecrm#dev-tools" onClick={handleLinkClick} className="nav-dropdown-item">
                  <div>
                    <strong>Docker & Dev Resources</strong>
                    <small>Production-ready containers & MCP server</small>
                  </div>
                </Link>
              </div>
            </li>
            <li><NavLink to="/use-cases" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Use Cases</NavLink></li>
            <li><NavLink to="/resources" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Resources</NavLink></li>
            <li><NavLink to="/contact" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Contact</NavLink></li>
          </ul>
          <div className="nav-actions">
            <a href="https://calendly.com/aakeeo-technologies/crm-consultations" target="_blank" rel="noopener noreferrer" className="nav-cta" onClick={() => trackCTAClick('nav_book_a_call', 'calendly')}>Book a Call</a>
          </div>
        </nav>

        {/* Mobile hamburger menu */}
        <button
          className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </header>

      {/* Mobile sidebar overlay */}
      <div className={`mobile-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={handleLinkClick}>
        <nav className="mobile-sidebar" onClick={(e) => e.stopPropagation()}>
          <ul>
            <li><NavLink to="/" onClick={handleLinkClick} end className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Home</NavLink></li>
            <li><NavLink to="/services" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Solutions</NavLink></li>
            <li><NavLink to="/suitecrm" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>SuiteCRM</NavLink></li>
            <li className="mobile-sub-links">
              <Link to="/services/suitecrm-implementation" onClick={handleLinkClick}>SuiteCRM Implementation</Link>
              <Link to="/services/suitecrm-customisation" onClick={handleLinkClick}>SuiteCRM Customisation</Link>
              <Link to="/services/crm-integration" onClick={handleLinkClick}>CRM Integration</Link>
              <Link to="/services/ai-workflow-automation" onClick={handleLinkClick}>AI Workflow Automation</Link>
              <Link to="/services/suitecrm-support" onClick={handleLinkClick}>Support & Maintenance</Link>
              <Link to="/suitecrm#dev-tools" onClick={handleLinkClick}>Docker & Dev Resources</Link>
            </li>
            <li><NavLink to="/use-cases" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Use Cases</NavLink></li>
            <li><NavLink to="/resources" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Resources</NavLink></li>
            <li><NavLink to="/contact" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Contact</NavLink></li>
            <li style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center' }}>
              <a href="https://calendly.com/aakeeo-technologies/crm-consultations" target="_blank" rel="noopener noreferrer" className="nav-cta" onClick={() => { trackCTAClick('mobile_book_a_call', 'calendly'); handleLinkClick(); }}>Book a Call</a>
            </li>
          </ul>
        </nav>
      </div>
    </>
  );
};

export default Navbar;